import tw from 'twin.macro';

// Development readout of the TCSA matcher (backend TCSA_DEBUG=1 sends `tcsaDebug` on every reply):
// which model and template, the state probabilities, where in the template the newest frame is, the
// count, and the latest form check. Non-interactive, like the hold telemetry pill, so it never eats a
// tap meant for the video. Renders nothing when the backend sends no readout.

const STATE_COLORS = { idle: '#9CA3AF', moving: '#38BDF8', holding: '#22C55E' };

const pct = value => (Number.isFinite(Number(value)) ? `${Math.round(Number(value) * 100)}%` : '-');

const Bar = ({ value, color }) => (
  <span tw="relative h-[5px] flex-1 overflow-hidden rounded-full bg-white/10">
    <span
      tw="absolute inset-y-0 left-0 rounded-full"
      style={{
        width: `${Math.max(0, Math.min(1, Number(value) || 0)) * 100}%`,
        backgroundColor: color,
      }}
    />
  </span>
);

const Row = ({ label, children }) => (
  <div tw="flex items-center gap-2 leading-none">
    <span tw="w-[58px] shrink-0 text-[9px] uppercase tracking-[0.08em] text-white/45">{label}</span>
    <span tw="flex flex-1 items-center gap-1.5 text-[11px] font-[700] tabular-nums text-white/90">
      {children}
    </span>
  </div>
);

const Section = ({ title, children }) => (
  <div tw="space-y-[4px] border-t border-white/10 pt-1.5">
    <p tw="text-[9px] font-[700] uppercase tracking-[0.14em] text-white/60">{title}</p>
    {children}
  </div>
);

const shortJoint = name =>
  String(name || '')
    .replace('left_', 'L ')
    .replace('right_', 'R ');

const TcsaDebugOverlay = ({ debug }) => {
  if (!debug || typeof debug !== 'object') {
    return null;
  }
  const state = debug.state || {};
  const alignment = debug.alignment || {};
  const form = debug.form;
  const moved = form?.static_moved || [];
  const furthest = form?.furthest_from_template || [];
  const watched = debug.watched_static || [];

  return (
    <div tw="pointer-events-none fixed right-4 top-24 z-[9000] w-[236px] space-y-1.5 rounded-[10px] border border-white/15 bg-black/65 px-2.5 py-2 font-mono text-white backdrop-blur-[3px] shadow-[0_6px_18px_rgba(0,0,0,0.35)]">
      <div tw="flex items-center gap-1.5">
        <span tw="text-[10px] font-[800] uppercase tracking-[0.14em] text-amber-300">TCSA</span>
        <span tw="truncate text-[10px] text-white/70">{debug.model || '-'}</span>
        <span tw="ml-auto text-[10px] text-white/50">
          {debug.shadow ? 'shadow' : debug.movement_type}
        </span>
      </div>
      <Row label="template">
        {debug.template_frames} fr
      </Row>

      <Section title="State">
        {['idle', 'moving', 'holding'].map(key => (
          <Row key={key} label={key}>
            <Bar value={state[key]} color={STATE_COLORS[key]} />
            <span tw="w-[30px] text-right">{pct(state[key])}</span>
          </Row>
        ))}
      </Section>

      <Section title="Alignment">
        <Row label="progress">
          <Bar value={alignment.progress} color="#F59E0B" />
          <span tw="w-[30px] text-right">{pct(alignment.progress)}</span>
        </Row>
        <Row label="frame">
          {alignment.template_frame ?? '-'} / {Math.max(0, (debug.template_frames || 1) - 1)}
          <span tw="ml-auto text-white/60">conf {pct(alignment.confidence)}</span>
        </Row>
      </Section>

      <Section title="Count">
        <Row label="reps">
          {debug.reps}
          {debug.shadow ? <span tw="text-white/60">(prod {debug.production_reps})</span> : null}
          <span tw="ml-auto text-white/60">
            {debug.in_hold
              ? `hold ${Number(debug.hold_s || 0).toFixed(1)}s`
              : debug.in_rep
                ? 'in rep'
                : 'idle'}
          </span>
        </Row>
      </Section>

      <Section
        title={form?.during_hold ? `Form · hold ${form.hold_s}s` : `Form · rep ${form?.rep ?? '-'}`}
      >
        <Row label="watched">
          <span tw="truncate text-white/70">
            {watched.length ? watched.map(shortJoint).join(', ') : 'none'}
          </span>
        </Row>
        {form ? (
          <>
            <Row label="moved">
              {moved.length ? (
                <span tw="text-red-400">{moved.map(shortJoint).join(', ')}</span>
              ) : (
                <span tw="text-green-400">none</span>
              )}
            </Row>
            {furthest.map(item => (
              <Row key={item.joint} label="off tpl">
                {shortJoint(item.joint)}
                <span tw="ml-auto text-white/60">{Number(item.torso_lengths).toFixed(2)}</span>
              </Row>
            ))}
          </>
        ) : (
          <p tw="text-[10px] text-white/50">after the first rep / during a hold</p>
        )}
      </Section>
    </div>
  );
};

export default TcsaDebugOverlay;
